<template>
    <v-snackbar
      :color="color"
      :timeout="notificationsTimeout"
      v-model="show"
      v-show="showPanelNotifications"
      bottom
      elevation="5"
      shaped
  >
    <div v-if="realTimeAPI">
        <span v-if="notification.roomType"> <b>@{{ notification.roomName }}</b> <span class="font-italic">({{ $t(notification.roomType) }})</span></span>
        <v-list-item>
            <v-list-item-icon>
                <v-badge color="orange" :value="notification.unread" offset-x="10" offset-y="10">
                    <span slot="badge"> {{ notification.unread }} </span>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                        <v-avatar size="40" v-on="on">
                            <img :src="notification.pictureProfileUrl">
                        </v-avatar>
                        </template>
                    </v-tooltip>
                </v-badge>
            </v-list-item-icon>
            <v-list-item-content style="max-width: 145px !important;">
                <v-list-item-title>
                    <span class="font-weight-regular text-body-1">{{ notification.name }}</span>
                </v-list-item-title>
                <v-list-item-subtitle><span class="font-weight-regular text-body-1">{{ notification.message }}</span></v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                plain
                color="orange"
                :to="{ name: 'chats', params: { roomType: roomType, roomId: roomId }}"
                @click="show=false"
              >
                {{ $t('chat.openChat') }}
              </v-btn>
              <v-btn
                plain
                @click="show=false"
              >
                {{ $t('chat.contextMenu.close') }}
              </v-btn>
            </v-list-item-action>
        </v-list-item>
        <audio ref="notificationSound" src="/chat_notification.mp3"></audio>
    </div>
  </v-snackbar>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { RealTimeAPI } from 'rocket.chat.realtime.api.rxjs'
import { NotifyMessageIn } from 'Store/notify'
import events from 'Store/events'

export default {
  name: 'chat-realtime',
  data () {
    return {
      show: false,
      color: '',
      realTimeAPI: null,
      subscriptions: [],
      roomType: null,
      roomId: null,
      notification: {
        pictureProfileUrl: null,
        unread: 0,
        message: '',
        name: '',
        roomType: null
      }
    }
  },
  created () {
    this.auth()
    window.addEventListener('beforeunload', () => this.realTimeAPI?.disconnect())
  },
  watch: {
    show () {
      if (this.show === false) {
        this.notification = {
          pictureProfileUrl: null,
          unread: 0,
          message: '',
          name: '',
          roomType: null,
          roomName: null
        }
      }
    },
    chatUser () {
      if (this.realTimeAPI === null) {
        this.openConnection()
      } else {
        this.reLoginRealtimeApi()
      }
    }
  },
  computed: {
    ...mapGetters({
      chatUser: 'chat/authenticatedRocketChatUser',
      getUnread: 'chat/unread',
      smsConversations: 'chat/smsConversations',
      directConversations: 'chat/directConversations',
      groups: 'chat/groups',
      channels: 'chat/channels',
      notifications: 'settings/notifications'
    }),
    unread: {
      get () { return this.getUnread },
      set (unread) { this.setUnread(unread) }
    },
    chatUrl () {
      return new URL(this.chatUser?.homeUrl)
    },
    browserNotificationGranted () {
      return typeof Notification !== 'undefined' && Notification.permission === 'granted'
    },
    showPanelNotifications () {
      return this.notifications?.enabled && !this.browserNotificationGranted
    },
    notificationsTimeout () {
      return this.notifications?.timeout || 2
    }
  },
  methods: {
    ...mapActions({
      auth: 'chat/auth',
      setUnread: 'chat/setUnread',
      listPresences: 'chat/listPresences',
      updatePresence: 'chat/updatePresence',
      updateLastSmsConversationMessage: 'chat/updateLastSmsConversationMessage',
      updateLastDirectConversationMessage: 'chat/updateLastDirectConversationMessage',
      updateLastChannelMessage: 'chat/updateLastChannelMessage',
      updateLastGroupMessage: 'chat/updateLastGroupMessage',
      getDirectMessages: 'chat/getDirectMessages',
      getSmsMessages: 'chat/getSmsMessages',
      setOpenSmsChat: 'chat/setOpenSmsChat',
      setOpenDmChat: 'chat/setOpenDmChat',
      setUnreadSmsChat: 'chat/setUnreadSmsChat',
      setUnreadDirectChat: 'chat/setUnreadDirectChat',
      setUnreadGroup: 'chat/setUnreadGroup',
      setUnreadChannel: 'chat/setUnreadChannel'
    }),

    /* Request notification of events and act on them */
    subscribeEvent (id, name, params, cb) {
      this.realTimeAPI.sendMessage({ msg: 'sub', id: id, name: name, params: params })
      this.realTimeAPI.subscribe((message) => {
        if (message?.msg === 'changed' && message.collection === name && message.fields?.eventName === params[0]) {
          cb(message)
        }
      })
    },

    /* Unsubscribe from an event notification */
    unsubscribeEvent (id, cb) {
      this.realTimeAPI.sendMessage({ msg: 'unsub', id: id })
      this.realTimeAPI.subscribe((message) => {
        if (message?.msg === 'nosub' && message.id === id) {
          cb(message)
        }
      })
    },

    /* Subscribe to new unread messages */
    subscribeUnread (chatUserID) {
      this.subscribeEvent('unread', 'stream-notify-user', [`${chatUserID}/subscriptions-changed`, false], (message) => {
        if (message?.fields?.args[1]?.u?._id === chatUserID) {
          const eventSubscription = message?.fields?.args[1]
          this.notification.unread = eventSubscription.unread
          this.notification.roomName = eventSubscription.name?.split('_')[1]
          const subExist = this.subscriptions.some((subscription) => subscription.rid === eventSubscription.rid)
          if (subExist) {
            this.subscriptions.forEach((subscription, index) => {
              if (subscription.rid === eventSubscription.rid) {
                if (eventSubscription.open) {
                  this.subscriptions[index].unread = eventSubscription.unread
                }
                this.subscriptions[index].open = eventSubscription.open
                this.refreshTotalUnread()
                const that = this
                // is this dm event is a sms thread?
                this.smsConversations.find((smsConversation, index) => {
                  if (smsConversation.id === eventSubscription.rid) {
                    const open = true
                    const unread = eventSubscription.unread
                    this.notification.name = smsConversation.formatChatUsername()
                    this.notification.pictureProfileUrl = smsConversation.to_user.contact_image || this.notification.pictureProfileUrl
                    that.setOpenSmsChat({ open, index })
                    that.setUnreadSmsChat({ unread, index })
                  }
                })
                // is this dm event is a chat thread?
                this.directConversations.find((directConversation, index) => {
                  if (directConversation.id === eventSubscription.rid) {
                    const open = true
                    const unread = eventSubscription.unread
                    this.notification.name = directConversation.formatChatUsername()
                    that.setOpenDmChat({ open, index })
                    that.setUnreadDirectChat({ unread, index })
                  }
                })
                // is this dm event is a chat thread?
                this.groups.find((group, index) => {
                  if (group.id === eventSubscription.rid) {
                    if (eventSubscription.open) {
                      const unread = eventSubscription.unread
                      that.setUnreadGroup({ unread, index })
                    }
                  }
                })
                // is this dm event is a chat thread?
                this.channels.find((channel, index) => {
                  if (channel.id === eventSubscription.rid) {
                    if (eventSubscription.open) {
                      const unread = eventSubscription.unread
                      that.setUnreadChannel({ unread, index })
                    }
                  }
                })
              }
            })
          } else {
            this.subscriptions.push(eventSubscription)
            this.refreshTotalUnread()
            if (eventSubscription.t === 'd') {
              this.getDirectMessages()
              this.getSmsMessages()
            }
          }
        }
      })
    },

    /* Subscribe to new user notifications */
    subscribeNotifications (chatUserID) {
      this.subscribeEvent('notification', 'stream-notify-user', [`${chatUserID}/notification`, false], (message) => {
        const data = message.fields.args[0]
        this.onReceiveMessage(data)
        // if ouside of the chat, display the notification with the snackbar
        if (!window.location.pathname.match('/chat') && message.fields?.args[0]?.payload?.message) {
          const msg = data.payload.message.msg
          const sender = data.payload.sender
          const fromUser = sender.name
          this.show = true
          this.notification = {
            pictureProfileUrl: this.profilePicture(sender.username),
            message: msg,
            name: fromUser,
            unread: this.notification.unread,
            roomType: this.getRoomType(data.payload.type)
          }
          // to redirect to the right conversation
          this.roomId = data.payload.rid
          this.roomType = this.getRawRoomType(data.payload.type)
          // play notif sound
          this.playNotificationSound()
          // browser notification
          if (this.notifications?.enabled && this.browserNotificationGranted) {
            this.browserNotification()
          }
        }
      })
    },

    browserNotification () {
      const unread = this.notification?.unread === 0 ? '' : ' (' + this.notification?.unread + ')'
      const title = `${this.notification?.name}${unread}`
      const body = this.notification?.message
      const tag = 'chat-notification'
      const avatar = this.notification?.pictureProfileUrl
      const actionUrl = `/chat/${this.roomType}/${this.roomId}`
      const timeout = this.notificationsTimeout
      NotifyMessageIn(tag, title, body, avatar, actionUrl, timeout)
    },

    // Function to play the notification sound
    playNotificationSound () {
      const sound = this.$refs.notificationSound
      if (sound) {
        sound.play().catch((error) => {
          events.pubError(`Error playing notification sound: ${error.message}`)
        })
      }
    },

    onReceiveMessage (data) {
      const notification = data
      if (notification.payload.type === 'd') {
        this.smsConversations.forEach((smsConversation, index) => {
          if (smsConversation.id === notification.payload.rid) {
            const params = { notification: notification.payload }
            this.updateLastSmsConversationMessage({ params, index })
          }
        })
        this.directConversations.forEach((directConversation, index) => {
          if (directConversation.id === notification.payload.rid) {
            const params = { notification: notification.payload }
            this.updateLastDirectConversationMessage({ params, index })
          }
        })
      } else if (notification.payload.type === 'c') {
        this.channels.forEach((channel, index) => {
          if (channel.id === notification.payload.rid) {
            const params = { notification: notification.payload }
            this.updateLastChannelMessage({ params, index })
          }
        })
      } else if (notification.payload.type === 'p') {
        this.groups.forEach((group, index) => {
          if (group.id === notification.payload.rid) {
            const params = { notification: notification.payload }
            this.updateLastGroupMessage({ params, index })
          }
        })
      }
    },

    /* Subscribe to presence update events */
    subscribePresence () {
      this.subscribeEvent('presence', 'stream-notify-logged', ['user-status', false], (message) => {
        const args = message.fields?.args[0]
        const presenceTypesMap = ['offline', 'online', 'away', 'busy']
        const presence = {
          userId: args[1],
          status: presenceTypesMap[args[2]],
          statusText: args[3]
        }
        // this only update the vuex store and wont make any query to mgm
        this.updatePresence(presence)
      })
    },

    openConnection () {
      const protocol = this.chatUrl.protocol === 'https:' ? 'wss' : 'ws'
      this.realTimeAPI = new RealTimeAPI(`${protocol}://${this.chatUrl.host}/websocket`)
      this.realTimeAPI.connectToServer()
      this.realTimeAPI.keepAlive().subscribe()
      this.loginRealtimeApi()
    },

    loginRealtimeApi () {
      this.realTimeAPI.loginWithAuthToken(this.chatUser?.authToken).subscribe(
        (data) => {
          if (data.msg === 'added') {
            this.subscribeUnread(data.id)
            this.subscribeNotifications(data.id)
            this.subscribePresence()
            this.getSubscriptions()
            this.listPresences()
          }
        }, (err) => this.dbg('Failed to subscribe to chat events: %o', err)
      )
    },

    reLoginRealtimeApi () {
      this.realTimeAPI.loginWithAuthToken(this.chatUser?.authToken)
    },

    /* Get a list of rooms, users, and groups a user needs events for */
    getSubscriptions () {
      const payload = { }
      this.realTimeAPI.callMethod('subscriptions/get', payload).subscribe(
        (data) => {
          this.subscriptions = data.result
          this.refreshTotalUnread()
        }, (err) => this.dbg('Failed to get chat user subscriptions: %o', err)
      )
    },

    refreshTotalUnread () {
      let unread = 0
      this.subscriptions.forEach((subscription) => {
        if (subscription.open) {
          unread += subscription.unread
          this.unread = unread
        }
      })
    },

    profilePicture (username) {
      const baseUrl = this.chatUrl.protocol + '//' + this.chatUrl.host
      return `${baseUrl}/avatar/${username}`
    },

    getRoomType (type) {
      if (type === 'c') {
        return 'chat.publicChannels'
      } else if (type === 'p') {
        return 'chat.privateChannels'
      } else {
        return null
      }
    },

    getRawRoomType (type) {
      return { c: 'channel', p: 'group', d: 'direct' }[type] || null
    }
  }
}
</script>

<style scoped>
</style>
